import React, { useState, useEffect } from 'react';
import { api } from '@/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Building2, Trash2 } from 'lucide-react';

const RELATIONSHIP_OPTIONS = ['hot', 'warm', 'neutral', 'cold'];
const RISK_RATINGS = [1, 2, 3, 4, 5];
const PAYMENT_BEHAVIORS = ['Good', 'Average', 'Poor'];

const CompanyModal = ({ open, company, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    industry: '',
    region: '',
    website: '',
    address: '',
    phone: '',
    email: '',
    relationship_strength: 'neutral',
    risk_rating: null,
    payment_behavior: '',
    notes: '',
    tags: [],
  });
  const [loading, setLoading] = useState(false);
  const [tagInput, setTagInput] = useState('');

  useEffect(() => {
    if (company) {
      setFormData({
        name: company.name || '',
        industry: company.industry || '',
        region: company.region || '',
        website: company.website || '',
        address: company.address || '',
        phone: company.phone || '',
        email: company.email || '',
        relationship_strength: company.relationship_strength || 'neutral',
        risk_rating: company.risk_rating || null,
        payment_behavior: company.payment_behavior || '',
        notes: company.notes || '',
        tags: company.tags || [],
      });
    } else {
      setFormData({
        name: '',
        industry: '',
        region: '',
        website: '',
        address: '',
        phone: '',
        email: '',
        relationship_strength: 'neutral',
        risk_rating: null,
        payment_behavior: '',
        notes: '',
        tags: [],
      });
    }
  }, [company, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Company name is required');
      return;
    }

    setLoading(true);
    try {
      if (company?.id) {
        await api.crm.updateCompany(company.id, formData);
        toast.success('Company updated successfully');
      } else {
        await api.crm.createCompany(formData);
        toast.success('Company created successfully');
      }
      onSave();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save company');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!company?.id) return;
    if (!window.confirm('Are you sure you want to delete this company? This will also delete all related contacts and opportunities.')) return;

    setLoading(true);
    try {
      await api.crm.deleteCompany(company.id);
      toast.success('Company deleted');
      onSave();
      onClose();
    } catch (error) {
      toast.error('Failed to delete company');
    } finally {
      setLoading(false);
    }
  };

  const addTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData({ ...formData, tags: [...formData.tags, tagInput.trim()] });
      setTagInput('');
    }
  };

  const removeTag = (tag) => {
    setFormData({ ...formData, tags: formData.tags.filter(t => t !== tag) });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-indigo-600" />
            {company ? 'Edit Company' : 'New Company'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label>Company Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., ABC Construction Ltd"
                data-testid="company-name-input"
              />
            </div>

            <div>
              <Label>Industry</Label>
              <Input
                value={formData.industry}
                onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                placeholder="e.g., Construction, Engineering"
              />
            </div>

            <div>
              <Label>Region</Label>
              <Input
                value={formData.region}
                onChange={(e) => setFormData({ ...formData, region: e.target.value })}
                placeholder="e.g., Gauteng, Western Cape"
              />
            </div>

            <div>
              <Label>Website</Label>
              <Input
                value={formData.website}
                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                placeholder="https://..."
              />
            </div>

            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="info@company.com"
              />
            </div>

            <div>
              <Label>Phone</Label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="+27 11 123 4567"
              />
            </div>

            <div>
              <Label>Relationship Strength</Label>
              <Select
                value={formData.relationship_strength}
                onValueChange={(value) => setFormData({ ...formData, relationship_strength: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {RELATIONSHIP_OPTIONS.map(opt => (
                    <SelectItem key={opt} value={opt}>{opt.charAt(0).toUpperCase() + opt.slice(1)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="col-span-2">
              <Label>Address</Label>
              <Textarea
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="Full company address"
                rows={2}
              />
            </div>

            <div>
              <Label>Risk Rating (1-5)</Label>
              <Select
                value={formData.risk_rating?.toString() || '__none__'}
                onValueChange={(value) => setFormData({ ...formData, risk_rating: value === '__none__' ? null : parseInt(value) })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select rating" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Not rated</SelectItem>
                  {RISK_RATINGS.map(r => (
                    <SelectItem key={r} value={r.toString()}>{r} - {r === 1 ? 'Very Low' : r === 2 ? 'Low' : r === 3 ? 'Medium' : r === 4 ? 'High' : 'Very High'}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Payment Behavior</Label>
              <Select
                value={formData.payment_behavior || '__none__'}
                onValueChange={(value) => setFormData({ ...formData, payment_behavior: value === '__none__' ? '' : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select behavior" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Not specified</SelectItem>
                  {PAYMENT_BEHAVIORS.map(b => (
                    <SelectItem key={b} value={b}>{b}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Accounting Contact Section */}
            <div className="col-span-2 border-t pt-4 mt-2">
              <p className="text-sm font-semibold text-slate-700 mb-3">Accounting Contact</p>
            </div>
            <div className="col-span-2">
              <Label>Contact Name</Label>
              <Input
                value={formData.accounting_contact_name}
                onChange={(e) => setFormData({ ...formData, accounting_contact_name: e.target.value })}
                placeholder="e.g., Jane Smith"
                data-testid="accounting-contact-name-input"
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={formData.accounting_contact_email}
                onChange={(e) => setFormData({ ...formData, accounting_contact_email: e.target.value })}
                placeholder="accounts@company.com"
                data-testid="accounting-contact-email-input"
              />
            </div>
            <div>
              <Label>Phone</Label>
              <Input
                value={formData.accounting_contact_phone}
                onChange={(e) => setFormData({ ...formData, accounting_contact_phone: e.target.value })}
                placeholder="+27 11 123 4567"
                data-testid="accounting-contact-phone-input"
              />
            </div>

            <div className="col-span-2">
              <Label>Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Any additional notes about this company..."
                rows={3}
              />
            </div>

            <div className="col-span-2">
              <Label>Tags</Label>
              <div className="flex gap-2 mb-2">
                <Input
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  placeholder="Add tag..."
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                />
                <Button type="button" variant="outline" onClick={addTag}>Add</Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.tags.map(tag => (
                  <span key={tag} className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 rounded text-sm">
                    {tag}
                    <button type="button" onClick={() => removeTag(tag)} className="text-slate-400 hover:text-slate-600">&times;</button>
                  </span>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter className="flex justify-between">
            <div>
              {company?.id && (
                <Button type="button" variant="destructive" onClick={handleDelete} disabled={loading}>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
              <Button type="submit" disabled={loading} data-testid="save-company-btn">
                {loading ? 'Saving...' : (company ? 'Update' : 'Create')}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CompanyModal;
